import isNumber from "lodash/isNumber";
import { useMemo } from "react";
import { Entity } from "../common/types/entity";
import {
    FinancialAccount,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "../common/types/financialAccount";
import { useEntitiesContexts } from "./useEntitiesContexts";

export interface UseFinancialAccountsOptions {
    entityId?: number;
    entityIds?: number[];
    excludeNoBalanceAccounts?: boolean;
    onlyBusiness?: boolean;
    excludeMockEntities?: boolean;
    sourceType?: FinancialAccountSourceType;
    connectionId?: number;
    connectionIds?: number[];
    createTempCashAccounts?: boolean;
    isSingleSelect?: boolean;
    excludeNonManualAccounts?: boolean;
}

export function usePlaidFinancialAccounts(
    options: UseFinancialAccountsOptions = {},
) {
    return useFinancialAccounts({
        ...options,
        sourceType: FinancialAccountSourceType.PLAID,
    });
}

export function usePayrollFinancialAccounts(
    options: UseFinancialAccountsOptions = {},
) {
    return useFinancialAccounts({
        ...options,
        sourceType: FinancialAccountSourceType.GUSTO,
    });
}

export function useFinancialAccounts({
    entityId,
    entityIds,
    sourceType,
    connectionId,
    connectionIds,
    excludeNoBalanceAccounts = false,
    onlyBusiness = false,
    excludeMockEntities = false,
    createTempCashAccounts = false,
    excludeNonManualAccounts = false,
}: UseFinancialAccountsOptions = {}) {
    connectionIds = useMemo(
        () => (connectionId ? [connectionId] : connectionIds),
        [connectionId, connectionIds],
    );
    entityIds = useMemo(
        () => (entityId ? [entityId] : entityIds),
        [entityId, entityIds],
    );

    const entitiesContexts = useEntitiesContexts({
        onlyBusiness,
        excludeMockEntities,
    });

    return useMemo(() => {
        let accounts = (entitiesContexts ?? [])
            .filter((ec) => !entityIds || entityIds.includes(ec.entity.id))
            .map((e) => e.financialAccounts)
            .flat();

        if (createTempCashAccounts) {
            const cashAccounts: FinancialAccount[] = (entitiesContexts ?? [])
                .filter((ec) => !entityIds || entityIds.includes(ec.entity.id))
                .filter((ec) =>
                    ec.financialAccounts.every(
                        (financialAccount) =>
                            financialAccount.financialType !==
                            FinancialAccountType.CASH,
                    ),
                )
                .map((ec) => ({
                    id: -1 * ec.entity.id,
                    isBusiness: ec.entity.isBusiness,
                    name: "Cash",
                    balance: 0,
                    balanceSyncedAt: new Date(),
                    transactionsSyncedAt: new Date(),
                    createdAt: new Date(),
                    entity: ec.entity,
                    entityId: ec.entity.id,
                    sourceType: FinancialAccountSourceType.MANUAL,
                    routingNumber: null,
                    wireRoutingNumber: null,
                    accountNumberMask: null,
                    disabled: false,
                    financialType: FinancialAccountType.CASH,
                    order: ec.financialAccounts.length + 1,
                    institutionName: null,
                }));

            accounts.push(...cashAccounts);
        }

        if (connectionIds) {
            accounts = accounts.filter((a) =>
                a.integrationAccounts
                    ?.map((ia) => ia.connectionId)
                    .some((cid) => cid && connectionIds.includes(cid)),
            );
        }
        if (excludeNoBalanceAccounts) {
            accounts = accounts.filter((a) => isNumber(a.balance));
        }
        if (sourceType) {
            accounts = accounts.filter((a) => a.sourceType === sourceType);
        }
        if (excludeNonManualAccounts) {
            accounts = accounts.filter(
                (account) =>
                    account.sourceType === FinancialAccountSourceType.MANUAL ||
                    account.sourceType ===
                        FinancialAccountSourceType.FILE_IMPORT,
            );
        }

        return accounts;
    }, [
        entitiesContexts,
        entityIds,
        connectionIds,
        excludeNoBalanceAccounts,
        sourceType,
        createTempCashAccounts,
        excludeNonManualAccounts,
    ]);
}

export function getTotalBalance(allAccounts: FinancialAccount[]) {
    return allAccounts
        .filter((acc) => isNumber(acc.balance))
        .reduce(($, acc) => $ + (acc.balance ?? 0), 0);
}

export function useTotalBalance(entity?: Entity) {
    const allAccounts = useFinancialAccounts({
        entityId: entity?.id,
    });

    return getTotalBalance(allAccounts);
}
